import { ISelectOption } from 'components/forms/Selects'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IMultiStopLegDatObjects } from 'types/graphqlTypes'
import { ICostControlConfig } from 'apps/spot-quote-control-panel/types/costControlConfig'
import { ISpotBiddingRule } from 'apps/spot-quote-control-panel/types/spotBiddingConfig'

export enum FUEL_MILEAGE {
  'dat_miles' = 'Dat Miles',
  'mapbox_miles' = 'Mapbox Miles',
  'pc_miler_miles' = 'PC Miler Miles',
  'scraper_miles' = 'Shipper Miles',
  'shipper_fuel_surcharge' = 'Shipper Fuel Surcharge',
}

export enum RPM_SOURCE {
  'shipper_fuel_matrix' = 'Stark Fuel Schedule',
  'FCE' = 'Transfix FCE Model',
  'SCP' = 'Spot Control Panel Setting',
  'shipper_fuel_surcharge' = 'Shipper Fuel Surcharge',
}

export enum COST_CALC_TOOLTIP {
  'shipper_fuel_matrix' = 'This is the shipper fuel schedule found in the Stark Shipper Profile.',
  'FCE' = 'This is a Transfix data science model projecting the cost of fuel. Used in spot quoting when a shipper uses BTF.',
  'SCP' = 'This is the shipper’s RPM manually set in the Spot Control Panel.',
  'fuel_surcharge' = 'This estimate is used to calculate the suggested line haul quote.',
  'dat_miles' = 'Mileage according to the DAT service.',
  'mapbox_miles' = 'Mileage according to the Mapbox service.',
  'pc_miler_miles' = 'Mileage according to the PC Miler service.',
  'scraper_miles' = 'Mileage according to the shipper, likely determined by an autobidder.',
  'shipper_fuel_surcharge' = 'This is a fuel surcharge amount according to the shipper, provided to Transfix in the price request.',
}

export interface IShipment {
  priceRequestId: number
  externalId: string
  shipperApplicationId: number
  shipperName: string
  pickupStartDate: string
  pickupCity: string
  pickupStateCode: string
  pickupZipcode: string
  deliveryCity: string
  deliveryStateCode: string
  deliveryZipcode: string
  pickupCoordinates: number
  deliveryCoordinates: number
  datMiles: number
  customerQuote: number
  quoteWithoutFuel: number
  quoteWithFuel: number
  dontBid: boolean
  datRateNotFound: boolean
  cmc: number
  highDatRate: number
  datRate: number
  lowDatRate: number
}

export interface ISpotRate {
  priceRequestId: number
  requestUuid: string
  rates: IRates
  metadata: IMetadata
}

export interface IRates {
  allIn: number
  linehaul: number
  datEstimatedAllIn: number
  datLowAllIn: number
  datHighAllIn: number
  appliedForwardLookingDatRate?: number
  transfixCostPredictionEstimate: number
  transfixCostPredictionTop: number
  transfixCostPredictionBottom: number
  transfixCostPrediction40Percent: number
}

export interface ISpotBiddingRules {
  appliedRules: ISpotBiddingRule[]
  appliedCustomRules: string[]
}

export interface IMetadata {
  pickupPostalCode?: string
  dsCostPremiumPct?: number
  pickupCity?: string
  pickupStateCode?: string
  deliveryPostalCode?: string
  deliveryCity?: string
  deliveryStateCode?: string
  mapboxMiles?: string
  datMiles?: string
  datType?: string
  additionalStops?: IAdditionalStops[]
  multiStopLegDatObjects?: IMultiStopLegDatObjects[]
  customRules?: string[]
  spotBiddingRules?: ISpotBiddingRules
  appliedCostControlConfig: ICostControlConfig
  strikePriceRules?: ISpotBiddingRules
  fuelSurchargeCalculation: IFuelSurchargeCalculation
  finalManualCostAdjustmentPercent?: number
  strategyChosen?: string
  multiStopBucketUsed: number
  createdAt?: string
  isDoNotBid?: boolean
  adjustedCostBasis?: number
}

export interface IEquipmentType extends ISelectOption<string> {}

export interface ILocation {
  city: string
  state: string
  postalCode: string
}

export interface IFuelSurchargeCalculation {
  fuelRpm?: number
  fuelRpmType?: RPM_SOURCE
  fuelMileageType?: FUEL_MILEAGE
  fuelMileage?: number
  calculatedFuelCost?: number
}

export interface IFormState {
  pickup?: ILocationSuggestion
  delivery?: ILocationSuggestion
  equipmentKey: IEquipmentType
  pickupStartDate?: string
  pickupStartTime?: string
  deliveryStartDate?: string
  deliveryStartTime?: string
  shipperApplicationId?: number
  shipperName?: string
  isTestQuote: boolean
  isGeneratedQuoteStored: boolean
  isManualQuoteStored: boolean
  customerQuote?: number
  manualQuoteOverrideReason?: string
  externalId?: string
  isManualAllin?: boolean
  isManualLinehaul?: boolean
  additionalStops: IAdditionalStops[]
}

export interface IAdditionalStops {
  city?: string
  state?: string
  countryCode?: string
  startDate?: string
  startTime?: string
  id: number
  stateCode?: string
  isoCountryCode?: string
  postalCode?: string
}

export interface IAmQuotingState {
  opportunity: IOpportunity
  spotRate?: ISpotRate
  fetching: boolean
  error?: string
}

export interface IOpportunity {
  externalId?: string
  shipperApplicationId?: string
  pickupStartDate?: string
  deliveryStartDate?: string
  shipperName?: string
  equipmentKey: string
  isTestQuote: string
}
